import { launchPlanClient } from './lib/launch-plan-client';

type DataRecord = Record<string, unknown>;

const importanceLabels: Record<string, string> = {
  S: '战役级波段',
  A: '重点波段',
  B: '常规波段',
  C: '补充波段',
};

const timelineFields: Array<[string, string, string]> = [
  ['整个波段开发周期', 'cycleStart', 'cycleEnd'],
  ['企划阶段', 'planningStart', 'planningEnd'],
  ['产品设计阶段', 'designStart', 'designEnd'],
  ['打样阶段', 'samplingStart', 'samplingEnd'],
  ['大货生产周期', 'bulkProductionStart', 'bulkProductionEnd'],
  ['包装生产周期', 'packagingProductionStart', 'packagingProductionEnd'],
  ['视觉设计周期', 'visualDesignStart', 'visualDesignEnd'],
  ['推广周期', 'promotionStart', 'promotionEnd'],
  ['商品上架周期', 'shelvingStart', 'shelvingEnd'],
];

const competitorReferenceDefinitions = [
  { id: 'heiress', title: '韩系小千金风' },
  { id: 'commuter', title: '韩系精致通勤风格' },
  { id: 'fresh', title: '韩系小清新风格' },
  { id: 'cool', title: '韩系冷感大女主风格' },
] as const;

const planAnalysisDefinitions = [
  ['category', '品类分析'],
  ['price', '价格分析'],
  ['level', '产品等级分析'],
  ['material', '材质分析'],
  ['craft', '工艺分析'],
  ['element', '元素分析'],
  ['board', '产品看板'],
  ['list', '产品清单'],
  ['showcase', '产品上身展示'],
] as const;

const standardPlanPriceBands = ['50元以下', '50–100元', '100–150元', '150–200元', '200–250元', '250–300元', '300元以上'] as const;

const octoberBrandLaunchPricePlan: Record<string, number[]> = {
  necklace: [0, 0, 3, 7, 4, 3, 0],
  bracelet: [0, 0, 0, 3, 3, 0, 0],
  earrings: [0, 6, 4, 3, 0, 0, 0],
  ring: [0, 2, 4, 0, 0, 0, 0],
};

let mounted = false;

const showcaseSaveTimers = new Map<string, ReturnType<typeof setTimeout>>();
let showcaseSaveChain: Promise<void> = Promise.resolve();
let productAttributeSaveChain: Promise<void> = Promise.resolve();

export function mountWaveReportPackaging(): () => void {
  if (mounted) return () => undefined;
  const pageContent = document.getElementById('pageContent');
  if (!pageContent) return () => undefined;
  mounted = true;
  pageContent.dataset.waveReportPackaging = 'ready';

  const installButtons = () => {
    pageContent.querySelectorAll<HTMLElement>('.launch-plan-card').forEach(card => {
      const actions = card.querySelector<HTMLElement>('.launch-plan-actions');
      const planId = card.dataset.launchPlanId || card.querySelector<HTMLButtonElement>('[data-launch-edit]')?.dataset.launchEdit;
      if (!actions || !planId || actions.querySelector('[data-wave-package]')) return;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'launch-action-btn wave-package-button';
      button.dataset.wavePackage = planId;
      button.setAttribute('aria-expanded', 'false');
      button.innerHTML = '<span aria-hidden="true">▣</span> 波段企划打包';
      actions.append(button);
      const analysis = document.createElement('div');
      analysis.className = 'launch-analysis-actions';
      analysis.setAttribute('aria-label', '企划分析');
      analysis.innerHTML = planAnalysisDefinitions.map(([key, label]) => `<button class="launch-analysis-btn" type="button" data-launch-analysis="${key}|${planId}" aria-expanded="false">${label}</button>`).join('');
      actions.append(analysis);
    });
  };

  let scheduled = false;
  const observer = new MutationObserver(() => {
    if (scheduled) return;
    scheduled = true;
    queueMicrotask(() => {
      scheduled = false;
      installButtons();
    });
  });
  observer.observe(pageContent, { childList: true, subtree: true });
  installButtons();

  const handleClick = async (event: Event) => {
    const target = event.target as Element | null;
    // Product-attribute selects use the browser's native picker.  Its option
    // popup is rendered outside the modal DOM, so Chromium can report the
    // follow-up click against the modal backdrop.  Never let that interaction
    // enter the board/modal click router.
    if (target?.closest('[data-product-attribute]')) return;
    const boardClose = target?.closest<HTMLButtonElement>('[data-board-modal-close]');
    if (boardClose) {
      boardClose.closest<HTMLElement>('.launch-board-modal')?.remove();
      pageContent.querySelectorAll<HTMLButtonElement>('[data-launch-analysis]').forEach(button => button.setAttribute('aria-expanded', 'false'));
      return;
    }
    if (target?.classList.contains('launch-board-modal')) {
      target.remove();
      pageContent.querySelectorAll<HTMLButtonElement>('[data-launch-analysis]').forEach(button => button.setAttribute('aria-expanded', 'false'));
      return;
    }
    const segmentScrollButton = target?.closest<HTMLButtonElement>('[data-wave-report-segment-scroll]');
    if (segmentScrollButton) {
      const report = segmentScrollButton.closest<HTMLElement>('.wave-plan-report');
      const rail = report?.querySelector<HTMLElement>('.wave-report-segments');
      if (rail) {
        const direction = segmentScrollButton.dataset.waveReportSegmentScroll === 'prev' ? -1 : 1;
        rail.scrollBy({ left: direction * Math.max(rail.clientWidth * 0.92, 360), behavior: 'smooth' });
      }
      return;
    }
    const themeToggle = target?.closest<HTMLButtonElement>('[data-wave-report-theme-toggle]');
    if (themeToggle) {
      const theme = themeToggle.closest<HTMLElement>('.wave-report-theme');
      if (!theme) return;
      const collapsed = theme.classList.toggle('is-collapsed');
      themeToggle.textContent = collapsed ? '＋' : '−';
      themeToggle.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
      themeToggle.setAttribute('aria-label', collapsed ? '展开主题内容' : '收起主题内容');
      return;
    }
    const preview = target?.closest<HTMLButtonElement>('[data-wave-report-image]');
    if (preview) {
      openReportLightbox(preview.dataset.waveReportImage ?? '', preview.dataset.waveReportAlt ?? '波段企划图片');
      return;
    }

    const showcaseRemove = target?.closest<HTMLButtonElement>('[data-showcase-remove-image]');
    if (showcaseRemove) {
      const modal = showcaseRemove.closest<HTMLElement>('.launch-board-modal');
      const panel = modal?.querySelector<HTMLElement>('.launch-analysis-panel');
      const planId = panel?.dataset.planId ?? '';
      const [kind, ...pathParts] = (showcaseRemove.dataset.showcaseRemoveImage ?? '').split('|');
      const imageIndex = Number(pathParts.pop());
      const itemPath = pathParts.join('|');
      if (!modal || !panel || !planId || !kind || !Number.isInteger(imageIndex)) return;
      showcaseRemove.disabled = true;
      await saveShowcasePlanChange(planId, modal, plan => {
        const item = showcaseItemAt(plan, itemPath);
        const imageField = showcaseImageField(itemPath, kind);
        const images = strings(item?.[imageField]);
        images.splice(imageIndex, 1);
        if (item) item[imageField] = images;
        if (kind === 'referenceImages' && item) {
          const details = records(item.referenceImageDetails);
          details.splice(imageIndex, 1);
          item.referenceImageDetails = details;
        }
      }, true);
      return;
    }

    const listSortButton = target?.closest<HTMLButtonElement>('[data-product-list-sort]');
    if (listSortButton) {
      const modal = listSortButton.closest<HTMLElement>('.launch-board-modal');
      const panel = modal?.querySelector<HTMLElement>('.launch-analysis-panel');
      const planId = panel?.dataset.planId;
      const field = listSortButton.dataset.productListSort || '';
      if (!modal || !panel || !planId || !field) return;
      let sorts: Array<{ field: string; direction: 'asc' | 'desc' }> = [];
      try { sorts = JSON.parse(modal.dataset.productListSort || '[]'); } catch { sorts = []; }
      const additive = (event as MouseEvent).shiftKey;
      const existingIndex = sorts.findIndex(sort => sort.field === field);
      const existingSort = existingIndex >= 0 ? sorts[existingIndex] : undefined;
      if (!additive) sorts = existingSort ? [{ field, direction: existingSort.direction === 'asc' ? 'desc' : 'asc' }] : [{ field, direction: 'asc' }];
      else if (existingSort && existingIndex >= 0) sorts[existingIndex] = { ...existingSort, direction: existingSort.direction === 'asc' ? 'desc' : 'asc' };
      else sorts.push({ field, direction: 'asc' });
      modal.dataset.productListSort = JSON.stringify(sorts);
      const plans = await launchPlanClient.list();
      const plan = plans.find(item => String(item.id ?? '') === planId);
      if (plan) {
        panel.innerHTML = renderPlanAnalysis('list', plan, sorts, { readOnly: launchPlanClient.isReadOnlyUser() });
        setupProductListScroll(modal);
      }
      return;
    }

    const annualProductButton = target?.closest<HTMLButtonElement>('[data-annual-plan-action="product"]');
    if (annualProductButton) {
      const monthKey = annualProductButton.dataset.annualMonth ?? '';
      annualProductButton.disabled = true;
      const originalLabel = annualProductButton.textContent ?? '产品开发计划';
      annualProductButton.textContent = '正在打开完整企划…';
      try {
        const plans = await launchPlanClient.list();
        const plan = findAnnualPlanForMonth(plans, monthKey);
        if (!plan) throw new Error(`没有找到与 ${formatAnnualMonth(monthKey)} 对应的产品开发计划。`);
        document.querySelector<HTMLElement>('.launch-board-modal')?.remove();
        const modal = document.createElement('div');
        modal.className = 'launch-board-modal annual-wave-report-modal';
        modal.innerHTML = `<div class="annual-wave-report-dialog" role="dialog" aria-modal="true" aria-label="${html(plain(plan.name) || formatAnnualMonth(monthKey))}完整波段企划"><button class="annual-wave-report-close" type="button" data-board-modal-close aria-label="关闭完整企划报告">×</button><section class="wave-plan-report annual-linked-wave-report">${renderWaveReport(plan)}</section></div>`;
        document.body.append(modal);
      } catch (error) {
        window.alert(error instanceof Error ? error.message : '完整产品开发企划打开失败');
      } finally {
        annualProductButton.disabled = false;
        annualProductButton.textContent = originalLabel;
      }
      return;
    }

    const analysisButton = target?.closest<HTMLButtonElement>('[data-launch-analysis]');
    if (analysisButton) {
      const [analysisKey, planId] = (analysisButton.dataset.launchAnalysis ?? '|').split('|');
      const card = analysisButton.closest<HTMLElement>('.launch-plan-card');
      if (!card || !analysisKey || !planId) return;
      const existingModal = document.querySelector<HTMLElement>('.launch-board-modal');
      if (existingModal) existingModal.remove();
      const plans = await launchPlanClient.list();
      const plan = plans.find(item => String(item.id ?? '') === planId);
      if (!plan) { window.alert('没有找到这份波段计划，请刷新页面后重试。'); return; }
      const title = planAnalysisDefinitions.find(([key]) => key === analysisKey)?.[1] || '企划分析';
      const modal = document.createElement('div');
      modal.className = `launch-board-modal${analysisKey === 'list' ? ' product-list-modal' : ''}`;
      if (analysisKey === 'list') modal.dataset.productListSort = '[]';
      const readOnly = launchPlanClient.isReadOnlyUser();
      modal.innerHTML = `<div class="launch-board-modal-dialog" role="dialog" aria-modal="true" aria-label="${html(title)}"><header><div><span>PLAN ANALYSIS / 企划二级分析</span><h2>${html(plain(plan.name) || '产品开发计划')} · ${html(title)}</h2></div><button type="button" data-board-modal-close aria-label="关闭企划分析">×</button></header><section class="launch-analysis-panel" data-analysis-key="${html(analysisKey)}" data-plan-id="${html(planId)}">${renderPlanAnalysis(analysisKey, plan, [], { readOnly })}</section></div>`;
      const closeModal = () => {
        modal.remove();
        pageContent.querySelectorAll<HTMLButtonElement>('[data-launch-analysis]').forEach(button => button.setAttribute('aria-expanded', 'false'));
      };
      modal.addEventListener('click', event => {
        const clicked = event.target as Element | null;
        if (clicked?.closest('[data-product-attribute]')) return;
        if (clicked === modal && modal.querySelector('[data-product-attribute]:focus')) return;
        if (clicked?.closest('[data-board-modal-close]') || clicked === modal) closeModal();
      });
      // Keep native select pointer/click events inside the control.  This is
      // intentionally event isolation only: no timers, showPicker(), focus
      // forcing, scroll locking, or synthetic dropdown behavior.
      modal.addEventListener('pointerdown', event => {
        if ((event.target as Element | null)?.closest('[data-product-attribute]')) event.stopPropagation();
      });
      modal.addEventListener('mousedown', event => {
        if ((event.target as Element | null)?.closest('[data-product-attribute]')) event.stopPropagation();
      });
      modal.addEventListener('click', event => {
        if ((event.target as Element | null)?.closest('[data-product-attribute]')) event.stopPropagation();
      });
      modal.addEventListener('change', handleBoardFilterChange);
      if (analysisKey === 'list' && !readOnly) {
        modal.addEventListener('change', event => { void handleProductAttributeChange(event, modal, planId); });
      }
      if (analysisKey === 'showcase' && !readOnly) {
        modal.addEventListener('change', event => { void handleShowcaseImageChange(event, modal, planId); });
        modal.addEventListener('input', event => handleShowcaseTextInput(event, modal, planId));
      }
      document.body.append(modal);
      if (analysisKey === 'list') setupProductListScroll(modal);
      pageContent.querySelectorAll<HTMLButtonElement>('[data-launch-analysis]').forEach(button => button.setAttribute('aria-expanded', button === analysisButton ? 'true' : 'false'));
      return;
    }

    const button = target?.closest<HTMLButtonElement>('[data-wave-package]');
    if (!button) return;
    const card = button.closest<HTMLElement>('.launch-plan-card');
    if (!card) return;

    const existing = card.querySelector<HTMLElement>('.wave-plan-report');
    if (existing) {
      existing.remove();
      card.classList.remove('wave-report-open');
      button.setAttribute('aria-expanded', 'false');
      button.innerHTML = '<span aria-hidden="true">▣</span> 波段企划打包';
      return;
    }

    const planId = button.dataset.wavePackage ?? '';
    button.disabled = true;
    button.textContent = '正在生成总报告…';
    try {
      const plans = await launchPlanClient.list();
      const plan = plans.find(item => String(item.id ?? '') === planId);
      if (!plan) throw new Error('没有找到这份波段计划，请刷新页面后重试。');
      const report = document.createElement('section');
      report.className = 'wave-plan-report';
      report.setAttribute('aria-label', `${plain(plan.name) || '波段'}企划总报告`);
      report.innerHTML = renderWaveReport(plan);
      card.append(report);
      card.classList.add('wave-report-open');
      button.setAttribute('aria-expanded', 'true');
      button.innerHTML = '<span aria-hidden="true">▣</span> 收起企划总报告';
      report.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch (error) {
      button.innerHTML = '<span aria-hidden="true">▣</span> 波段企划打包';
      window.alert(error instanceof Error ? error.message : '波段企划总报告生成失败');
    } finally {
      button.disabled = false;
    }
  };
  const handleBoardFilterChange = async (event: Event) => {
    const select = (event.target as Element | null)?.closest<HTMLSelectElement>('[data-board-filter]');
    if (!select) return;
    const panel = select.closest<HTMLElement>('.launch-analysis-panel');
    const results = panel?.querySelector<HTMLElement>('[data-board-results]');
    const planId = panel?.dataset.planId;
    if (!panel || !results || !planId) return;
    const plans = await launchPlanClient.list();
    const plan = plans.find(item => String(item.id ?? '') === planId);
    if (!plan) return;
    const filters = Object.fromEntries([...panel.querySelectorAll<HTMLSelectElement>('[data-board-filter]')].map(item => [item.dataset.boardFilter ?? '', item.value])) as Record<string, string>;
    results.innerHTML = renderBoardGroups(filterBoardRecords(collectPlanRecords(plan), filters), filters.group || 'category');
  };
  document.addEventListener('click', handleClick);
  document.addEventListener('change', handleBoardFilterChange);

  return () => {
    observer.disconnect();
    document.removeEventListener('click', handleClick);
    document.removeEventListener('change', handleBoardFilterChange);
    showcaseSaveTimers.forEach(timer => clearTimeout(timer));
    showcaseSaveTimers.clear();
    delete pageContent.dataset.waveReportPackaging;
    mounted = false;
  };
}

export function findAnnualPlanForMonth(plans: DataRecord[], monthKey: string): DataRecord | undefined {
  const match = /^(\d{4})-(\d{2})$/.exec(monthKey);
  if (!match) return undefined;
  const [, year, paddedMonth] = match;
  const chineseMonth = `${year}年${Number(paddedMonth)}月`;
  const dottedMonth = `${year}.${paddedMonth}`;
  const named = plans.filter(plan => {
    const name = plain(plan.name);
    return name.includes(chineseMonth) || name.includes(monthKey) || name.includes(dottedMonth);
  });
  const candidates = named.length
    ? named
    : plans.filter(plan => plain(plan.launchDate).startsWith(monthKey));
  return candidates.sort((left, right) => {
    const leftPriority = /品牌首发/.test(plain(left.name)) ? 1 : 0;
    const rightPriority = /品牌首发/.test(plain(right.name)) ? 1 : 0;
    return rightPriority - leftPriority || plain(right.updatedAt).localeCompare(plain(left.updatedAt));
  })[0];
}

function formatAnnualMonth(monthKey: string): string {
  const match = /^(\d{4})-(\d{2})$/.exec(monthKey);
  return match ? `${match[1]}年${Number(match[2])}月` : monthKey || '该月份';
}

function renderPlanAnalysis(key: string, plan: DataRecord, listSorts: Array<{ field: string; direction: 'asc' | 'desc' }> = [], options: { readOnly?: boolean } = {}): string {
  const title = planAnalysisDefinitions.find(([item]) => item === key)?.[1] ?? '企划分析';
  const recordsList = collectPlanRecords(plan);
  if (key === 'board') return analysisPanel(title, renderProductBoard(recordsList));
  if (key === 'list') return analysisPanel(title, renderProductList(recordsList, listSorts, options));
  if (key === 'showcase') return analysisPanel(title, renderProductWearShowcase(plan, options));
  if (key === 'category') {
    const matrix = record(plan.assortmentMatrix);
    const categories = Array.isArray(matrix.categories) ? matrix.categories.map(plain).filter(Boolean) : [];
    const rows = records(matrix.rows);
    const quantities = (row: DataRecord): unknown[] => Array.isArray(row.quantities) ? row.quantities : [];
    const planRows = rows.map(row => ({ label: plain(row.style) || '未命名风格', values: categories.map((_, index) => Number(quantities(row)[index]) || 0) }));
    const actualColumns = uniqueValues(recordsList.map(item => item.category), '未填写品类');
    const actualRows = countMatrix(recordsList, item => item.styleLine || '未命名风格线', item => item.category || '未填写品类', actualColumns);
    const plannedTotal = planRows.reduce((sum, row) => sum + row.values.reduce((total, value) => total + value, 0), 0);
    return comparisonAnalysisPanel(title, '计划品类与实际开款品类、风格线的数量对比', comparisonMetrics(plannedTotal, recordsList.length), matrixTable('计划：品类 × 风格', categories, planRows, '合计'), matrixTable('实际：品类 × 风格线', actualColumns, actualRows, '实际合计'));
  }
  if (key === 'price') {
    const matrix = record(plan.assortmentMatrix);
    const categories = Array.isArray(matrix.categories) ? matrix.categories.map(plain).filter(Boolean) : [];
    const rows = records(matrix.rows);
    const unmatchedBand = '未填写价格段';
    const hasUnmatchedQuantities = rows.some(row => {
      const quantities = Array.isArray(row.quantities) ? row.quantities : [];
      return !normalizePlanPriceBand(plain(row.priceRange)) && quantities.some(value => (Number(value) || 0) > 0);
    });
    const bands = [...standardPlanPriceBands, ...(hasUnmatchedQuantities ? [unmatchedBand] : [])];
    const plannedRows = categories.map((category, categoryIndex) => {
      const presetValues = octoberBrandLaunchPlannedPriceValues(plain(plan.name), category);
      return {
        label: category || `品类${categoryIndex + 1}`,
        values: presetValues ?? bands.map(band => rows.reduce((sum, row) => {
        const normalizedBand = normalizePlanPriceBand(plain(row.priceRange)) || unmatchedBand;
        return normalizedBand === band
          ? sum + (Number((Array.isArray(row.quantities) ? row.quantities : [])[categoryIndex]) || 0)
          : sum;
        }, 0)),
      };
    });
    const unmatchedActualBand = '未填写价格';
    const hasUnmatchedActualPrices = recordsList.some(item => !normalizePlanPriceBand(item.price || ''));
    const actualColumns = [...standardPlanPriceBands, ...(hasUnmatchedActualPrices ? [unmatchedActualBand] : [])];
    const actualRows = countMatrix(
      recordsList,
      item => item.category || '未填写品类',
      item => normalizePlanPriceBand(item.price || '') || unmatchedActualBand,
      actualColumns,
    );
    const plannedTotal = plannedRows.reduce((sum, row) => sum + row.values.reduce((total, value) => total + value, 0), 0);
    return comparisonAnalysisPanel(title, '计划价格段权重与实际各品类预计售价的数量对比', comparisonMetrics(plannedTotal, recordsList.length), matrixTable('计划：品类 × 价格段', bands, plannedRows, '计划合计'), matrixTable('实际：品类 × 预计售价', actualColumns, actualRows, '实际合计'));
  }
  if (key === 'level') {
    const levels = ['S', 'A', 'B', 'C'];
    const categoryRows = countMatrix(recordsList, item => item.category || '未填写品类', item => item.level || '未定级', levels);
    const styleRows = countMatrix(recordsList, item => item.styleLine || '未命名风格线', item => item.level || '未定级', levels);
    return comparisonAnalysisPanel(title, '按实际已录入款式统计产品等级，品类在上、风格线在下', matrixTable('实际：品类 × 产品等级', levels, categoryRows, '合计'), matrixTable('实际：风格线 × 产品等级', levels, styleRows, '合计'));
  }
  if (key === 'material' || key === 'element') {
    const fieldName = key === 'material' ? 'material' : 'element';
    const label = key === 'material' ? '材质' : '设计元素';
    const columns = uniqueValues(recordsList.map(item => item[fieldName]), `未填写${label}`);
    const rows = countMatrix(recordsList, item => item.category || '未填写品类', item => item[fieldName] || `未填写${label}`, columns);
    return comparisonAnalysisPanel(title, `按实际开款记录统计品类与${label}的数量分布`, matrixTable(`实际：品类 × ${label}`, columns, rows, '合计'));
  }
  if (key === 'board') return analysisPanel(title, `<div class="launch-analysis-board">${recordsList.length ? recordsList.map((item, index) => `<article><div class="launch-analysis-board-media">${item.image ? renderBoardImage(item.image, item.name || `产品 ${index + 1}`) : '<span>暂无图片</span>'}</div><strong>${html(item.name || `产品 ${index + 1}`)}</strong><p>${html([item.category, item.style, item.price].filter(Boolean).join(' · ') || '尚未填写产品信息')}</p></article>`).join('') : emptyAnalysis('尚未建立产品记录')}</div>`);
  const fieldMap: Record<string, [string, string]> = {
    price: ['计划价格', 'price'], level: ['产品等级', 'level'], material: ['材质', 'material'], craft: ['工艺', 'craft'], element: ['元素', 'element'],
  };
  const [label, fieldName] = fieldMap[key] ?? ['分析', 'style'];
  const counts = new Map<string, number>();
  recordsList.forEach(item => { const value = item[fieldName] || '未填写'; counts.set(value, (counts.get(value) || 0) + 1); });
  return analysisPanel(title, `<div class="launch-analysis-values">${[...counts.entries()].map(([value, count]) => `<div><strong>${count}</strong><span>${html(value)}</span></div>`).join('') || emptyAnalysis('尚未建立产品记录')}</div>`);
}

function uniqueValues(values: Array<string | undefined>, fallback: string): string[] {
  const result = [...new Set(values.map(value => String(value || '').trim()).filter(Boolean))];
  return result.length ? result : [fallback];
}

export function sortPriceColumns(columns: string[]): string[] {
  const numericValue = (value: string): number => {
    const match = value.replace(/,/g, '').match(/\d+(?:\.\d+)?/);
    return match ? Number(match[0]) : Number.POSITIVE_INFINITY;
  };
  return [...columns].sort((left, right) => {
    const numericDifference = numericValue(left) - numericValue(right);
    return Number.isNaN(numericDifference) || numericDifference === 0
      ? left.localeCompare(right, 'zh-CN', { numeric: true })
      : numericDifference;
  });
}

export function normalizePlanPriceBand(value: string): string {
  const normalized = String(value || '').trim().replace(/,/g, '').replace(/[—－~～至]/g, '-');
  const values = normalized.match(/\d+(?:\.\d+)?/g)?.map(Number).filter(Number.isFinite) ?? [];
  if (!values.length) return '';
  const firstValue = values[0] ?? 0;
  let representative = firstValue;
  if (/以下|以内|不超过/.test(normalized)) representative = Math.max(0, firstValue - 0.01);
  else if (values.length >= 2) representative = (firstValue + (values[1] ?? firstValue)) / 2;
  if (representative < 50) return standardPlanPriceBands[0];
  if (representative < 100) return standardPlanPriceBands[1];
  if (representative < 150) return standardPlanPriceBands[2];
  if (representative < 200) return standardPlanPriceBands[3];
  if (representative < 250) return standardPlanPriceBands[4];
  if (representative < 300) return standardPlanPriceBands[5];
  return standardPlanPriceBands[6];
}

export function octoberBrandLaunchPlannedPriceValues(planName: string, category: string): number[] | undefined {
  if (!/2026\s*年?\s*10\s*月/.test(planName) || !planName.includes('品牌首发')) return undefined;
  const normalizedCategory = category.replace(/\s/g, '');
  const key = normalizedCategory.includes('项链')
    ? 'necklace'
    : normalizedCategory.includes('手链') || normalizedCategory.includes('手镯')
      ? 'bracelet'
      : normalizedCategory.includes('耳环') || normalizedCategory.includes('耳钉')
        ? 'earrings'
        : normalizedCategory.includes('戒指')
          ? 'ring'
          : '';
  const values = key ? octoberBrandLaunchPricePlan[key] : undefined;
  return values ? [...values] : undefined;
}

function countMatrix(recordsList: Array<Record<string, string>>, rowGetter: (item: Record<string, string>) => string, columnGetter: (item: Record<string, string>) => string, columns: string[]): Array<{ label: string; values: number[] }> {
  const map = new Map<string, number[]>();
  recordsList.forEach(item => {
    const row = rowGetter(item) || '未填写';
    const column = columnGetter(item) || '未填写';
    if (!map.has(row)) map.set(row, Array.from({ length: columns.length }, () => 0));
    const index = columns.indexOf(column);
    if (index >= 0) {
      const values = map.get(row);
      if (values) values[index] = (values[index] || 0) + 1;
    }
  });
  return [...map.entries()].map(([label, values]) => ({ label, values }));
}

function matrixTable(caption: string, columns: string[], rows: Array<{ label: string; values: number[] }>, totalLabel: string): string {
  const totals = columns.map((_, index) => rows.reduce((sum, row) => sum + (row.values[index] || 0), 0));
  const grandTotal = totals.reduce((sum, value) => sum + value, 0);
  return `<div class="launch-analysis-matrix launch-analysis-matrix-wide"><div class="launch-analysis-table-caption">${html(caption)}</div><table><thead><tr><th>维度</th>${columns.map(column => `<th>${html(column)}</th>`).join('')}<th>${html(totalLabel)}</th></tr></thead><tbody>${rows.length ? rows.map(row => `<tr><th>${html(row.label)}</th>${row.values.map(value => `<td>${value}</td>`).join('')}<td class="matrix-total">${row.values.reduce((sum, value) => sum + value, 0)}</td></tr>`).join('') : `<tr><td colspan="${columns.length + 2}">${emptyAnalysis('尚未建立产品记录')}</td></tr>`}<tr class="matrix-grand-total"><th>合计</th>${totals.map(value => `<td>${value}</td>`).join('')}<td>${grandTotal}</td></tr></tbody></table></div>`;
}

function comparisonMetrics(planned: number, actual: number): string {
  const difference = actual - planned;
  const completion = planned > 0 ? Math.round(actual / planned * 100) : 0;
  const warning = difference > 0 ? `<span class="is-warning">超出计划 ${difference} 款</span>` : '<span>未超出计划</span>';
  return `<div class="launch-analysis-metrics"><div><b>${planned}</b><span>计划款数</span></div><div><b>${actual}</b><span>实际款数</span></div><div><b>${completion}%</b><span>完成率</span></div><div><b>${difference > 0 ? '+' : ''}${difference}</b><span>计划差异</span></div><div>${warning}<small>超出预警</small></div></div>`;
}

function comparisonAnalysisPanel(title: string, description: string, ...tables: string[]): string {
  return `<section class="launch-analysis-panel-inner launch-analysis-comparison"><header><div><strong>${html(title)}</strong><p>${html(description)}</p></div><span>当前企划实时汇总</span></header><div class="launch-analysis-comparison-body">${tables.join('')}</div></section>`;
}

function collectPlanRecords(plan: DataRecord): Array<Record<string, string>> {
  const result: Array<Record<string, string>> = [];
  for (const [segmentIndex, segment] of records(plan.segments).entries()) for (const [sceneIndex, scene] of records(segment.scenes).entries()) for (const [themeIndex, theme] of records(scene.themes).entries()) {
    const styles = records(theme.styles);
    const styleImages = styles.flatMap(style => strings(style.images));
    const themeImages = strings(theme.finalImages);
    // Every distinct uploaded finished-product image is one actual SKU record.
    // Metadata rows are matched by position when available; this avoids the
    // old failure mode where three style rows hid six or more uploaded images.
    const images = [...styleImages, ...themeImages.filter(image => !styleImages.includes(image))];
    const sourceCount = images.length > 0 ? images.length : styles.length;
    for (let styleIndex = 0; styleIndex < sourceCount; styleIndex += 1) {
      const style = styles[styleIndex] ?? {};
      result.push({ productPath: `${segmentIndex}:${sceneIndex}:${themeIndex}:${styleIndex}`, name: plain(style.name) || `未命名款式 ${String(styleIndex + 1).padStart(2, '0')}`, designer: plain(style.designer) || plain(theme.designer), category: plain(style.category) || plain(style.productCategory) || plain(theme.category), style: plain(style.style) || plain(theme.name), theme: plain(style.theme) || plain(theme.name), styleLine: plain(segment.label), price: plain(style.plannedPrice) || plain(style.actualPrice) || plain(theme.priceBand), plannedRetailPrice: plain(style.plannedPrice), plannedDevelopmentPrice: plain(style.actualPrice), level: plain(style.productLevel) || plain(theme.productLevel), material: plain(style.material) || plain(theme.materials), craft: plain(theme.crafts), element: plain(style.elements) || plain(theme.elements), productAttribute: plain(style.productAttribute), outfit: plain(style.outfit), image: images[styleIndex] || '' });
    }
  }
  for (const [collection, products] of [['basicProducts', records(plan.basicProducts)], ['subwaveBasicProducts', records(plan.subwaveBasicProducts)] ] as Array<[string, DataRecord[]]>) for (const [productIndex, product] of products.entries()) result.push({ productPath: `basic|${collection}|${productIndex}`, name: plain(product.name) || '基本款', designer: plain(product.designer), category: plain(product.category) || plain(product.productCategory), style: plain(product.style), theme: plain(product.theme), styleLine: '子波段基本款', price: plain(product.plannedRetailPrice) || plain(product.plannedPrice) || plain(product.actualPrice), plannedRetailPrice: plain(product.plannedRetailPrice) || plain(product.plannedPrice), plannedDevelopmentPrice: plain(product.actualPrice), level: plain(product.productLevel), material: plain(product.material) || plain(product.plannedMaterial), craft: plain(product.craft) || plain(product.plannedCraft), element: plain(product.elements) || plain(product.plannedElements), productAttribute: plain(product.productAttribute), outfit: plain(product.outfit), image: strings(product.images)[0] || '' });
  return result;
}

function productAttributeItemAt(plan: DataRecord, path: string): DataRecord | undefined {
  if (path.startsWith('basic|')) {
    const [, collection, index] = path.split('|');
    if (!collection || index === undefined) return undefined;
    const products = Array.isArray(plan[collection]) ? plan[collection] as unknown[] : [];
    const productIndex = Number(index);
    if (!Number.isInteger(productIndex) || productIndex < 0 || productIndex >= products.length) return undefined;
    return record(products[productIndex]);
  }
  const [segmentIndex = Number.NaN, sceneIndex = Number.NaN, themeIndex = Number.NaN, styleIndex = Number.NaN] = path.split(':').map(Number);
  if ([segmentIndex, sceneIndex, themeIndex, styleIndex].some(value => !Number.isInteger(value))) return undefined;
  const segments = Array.isArray(plan.segments) ? plan.segments as unknown[] : [];
  const segment = record(segments[segmentIndex]);
  const scenes = Array.isArray(segment.scenes) ? segment.scenes as unknown[] : [];
  const scene = record(scenes[sceneIndex]);
  const themes = Array.isArray(scene.themes) ? scene.themes as unknown[] : [];
  const theme = record(themes[themeIndex]);
  if (!Object.keys(theme).length) return undefined;
  if (!Array.isArray(theme.styles)) theme.styles = [];
  const styles = theme.styles as unknown[];
  while (styles.length <= styleIndex) styles.push({});
  return record(styles[styleIndex]);
}

export function renderProductBoard(recordsList: Array<Record<string, string>>): string {
  // The product board is an actual-product gallery. Reference-only records
  // (especially basic products with no post-development image) must not create
  // empty placeholder cards here.
  const boardRecords = recordsList.filter(item => Boolean(item.image));
  const categories = [...new Set(boardRecords.map(item => item.category).filter((value): value is string => Boolean(value)))];
  // Product records already carry their parent style line from the launch-plan
  // segment. The board should use that source field directly instead of asking
  // collaborators to define a second, product-level style label.
  const styles = [...new Set(boardRecords.map(item => item.styleLine).filter((value): value is string => Boolean(value)))];
  const designers = [...new Set(boardRecords.map(item => item.designer).filter((value): value is string => Boolean(value)))];
  if (boardRecords.some(item => !item.designer)) designers.push('未指定设计师');
  return `<div class="launch-product-board"><div class="launch-product-board-controls">
    ${boardSelect('category', '品类筛选', categories)}
    ${boardSelect('style', '风格线筛选', styles)}
    ${boardSelect('designer', '设计师筛选', designers)}
    <label><span>分组方式</span><select data-board-filter="group"><option value="category">按品类分组</option><option value="style">按风格分组</option><option value="designer">按设计师分组</option></select></label>
    <label><span>排序方式</span><select data-board-filter="sort"><option value="level">等级 S → A → B → C</option><option value="price-desc">价格从高到低</option><option value="price-asc">价格从低到高</option></select></label>
  </div><div data-board-results>${renderBoardGroups(boardRecords)}</div></div>`;
}

export function renderProductWearShowcase(source: DataRecord | Array<Record<string, string>>, options: { readOnly?: boolean } = {}): string {
  if (Array.isArray(source)) return renderLegacyWearShowcase(source);
  const groups = showcaseGroups(source);
  if (!groups.length) return emptyAnalysis('尚未建立可展示的风格与主题');
  const readOnly = Boolean(options.readOnly);
  return `<div class="launch-product-wear-showcase">
    <div class="launch-product-wear-heading"><div><strong>产品上身展示</strong><p>按风格查看每个主题的参考款、改款成品与模特上身效果；本页不再按场景分层。</p></div><span class="launch-showcase-save-status${readOnly ? ' is-readonly' : ''}" aria-live="polite">${readOnly ? 'BOSS只读模式' : '修改后自动保存'}</span></div>
    <div class="launch-product-wear-series">${groups.map((group, groupIndex) => `<section class="launch-product-wear-style">
      <header><span>STYLE ${String(groupIndex + 1).padStart(2, '0')}</span><h3>${html(group.label)}</h3><em>${group.themes.length} 个主题</em></header>
      <div class="launch-product-wear-themes">${group.themes.map((theme, themeIndex) => renderShowcaseTheme(theme, themeIndex, readOnly)).join('')}</div>
    </section>`).join('')}</div>
  </div>`;
}

function renderLegacyWearShowcase(recordsList: Array<Record<string, string>>): string {
  const items = recordsList.filter(item => Boolean(item.image));
  if (!items.length) return emptyAnalysis('尚未上传可用于上身展示的实际成品图');
  return `<div class="launch-product-wear-showcase"><p class="launch-product-wear-intro">共 ${items.length} 款实际开发产品；点击图片可放大。</p><div class="launch-product-wear-grid">${items.map((item, index) => {
    const title = item.name || `产品 ${String(index + 1).padStart(2, '0')}`;
    const image = item.image || '';
    return `<article class="launch-product-wear-card"><div class="launch-product-wear-media">${renderBoardImage(image, `${title}上身展示`)}</div><div class="launch-product-wear-copy"><div><span>${String(index + 1).padStart(2, '0')}</span><h3>${html(title)}</h3></div><p class="launch-product-wear-meta">${html([item.styleLine, item.category, item.level].filter(Boolean).join(' · ') || '尚未填写产品归属')}</p>${item.outfit ? `<p class="launch-product-wear-outfit">${html(item.outfit)}</p>` : ''}</div></article>`;
  }).join('')}</div></div>`;
}

type ShowcaseTheme = { path: string; name: string; designMeaning: string; referenceImages: string[]; referenceImageDetails: DataRecord[]; finalImages: string[]; wearImages: string[] };

function showcaseGroups(plan: DataRecord): Array<{ label: string; themes: ShowcaseTheme[] }> {
  const groups = records(plan.segments).map((segment, segmentIndex) => ({
    label: plain(segment.label) || `风格 ${String(segmentIndex + 1).padStart(2, '0')}`,
    themes: records(segment.scenes).flatMap((scene, sceneIndex) => records(scene.themes).map((theme, themeIndex) => ({
      path: `${segmentIndex}:${sceneIndex}:${themeIndex}`,
      name: plain(theme.name) || `未命名主题 ${String(themeIndex + 1).padStart(2, '0')}`,
      designMeaning: plain(theme.designMeaning),
      referenceImages: strings(theme.referenceImages),
      referenceImageDetails: records(theme.referenceImageDetails),
      finalImages: strings(theme.finalImages),
      wearImages: strings(theme.wearImages),
    }))),
  })).filter(group => group.themes.length);
  const basics = [
    ...records(plan.basicProducts).map((product, index) => ({ product, path: `basic|cold|${index}` })),
    ...records(plan.subwaveBasicProducts).map((product, index) => ({ product, path: `basic|subwave|${index}` })),
  ];
  if (basics.length) groups.push({
    label: '子波段基本款',
    themes: basics.map(({ product, path }, index) => ({
      path,
      name: plain(product.name) || `基本款 ${String(index + 1).padStart(2, '0')}`,
      designMeaning: plain(product.designMeaning),
      referenceImages: strings(product.referenceImages),
      referenceImageDetails: records(product.referenceImageDetails),
      finalImages: strings(product.images),
      wearImages: strings(product.wearImages),
    })),
  });
  return groups;
}

function renderShowcaseTheme(theme: ShowcaseTheme, themeIndex: number, readOnly = false): string {
  const meaning = readOnly
    ? `<div class="launch-showcase-theme-meaning launch-showcase-theme-meaning-readonly"><span>设计主题寓意</span><p>${html(theme.designMeaning || '暂无填写')}</p></div>`
    : `<label class="launch-showcase-theme-meaning"><span>设计主题寓意</span><textarea data-showcase-theme-field="${theme.path}|designMeaning" placeholder="填写这个设计主题所表达的寓意、情绪与故事">${html(theme.designMeaning)}</textarea></label>`;
  return `<article class="launch-showcase-theme"><header><span>主题 ${String(themeIndex + 1).padStart(2, '0')}</span><h4>${html(theme.name)}</h4></header>${meaning}<div class="launch-showcase-journey">
    ${renderShowcaseGallery(theme, 'referenceImages', '01 · 开发参考图', '填写参考品牌与月销，说明为什么选择这款作为方向参考。', readOnly)}
    ${renderShowcaseGallery(theme, 'finalImages', '02 · 改款完成图', '呈现从参考款完成设计改款后的实际产品或设计稿。', readOnly)}
    ${renderShowcaseGallery(theme, 'wearImages', '03 · 模特上身图', '呈现产品实际佩戴到模特身上的比例、气质与整体效果。', readOnly)}
  </div></article>`;
}

function renderShowcaseGallery(theme: ShowcaseTheme, kind: 'referenceImages' | 'finalImages' | 'wearImages', title: string, tip: string, readOnly = false): string {
  const images = theme[kind];
  const visibleImages = images.slice(0, Math.max(10, images.length));
  return `<section class="launch-showcase-stage"><header><h5>${title}</h5><span>${images.length} / 10 张</span></header><p>${tip}</p><div class="launch-showcase-image-list">${visibleImages.map((src, imageIndex) => {
    const detail = theme.referenceImageDetails[imageIndex] ?? {};
    return `<article class="launch-showcase-image-card"><div class="launch-showcase-image-wrap">${renderBoardImage(src, `${theme.name} ${title} ${imageIndex + 1}`)}${readOnly ? '' : `<button type="button" data-showcase-remove-image="${kind}|${theme.path}|${imageIndex}" aria-label="删除这张图片">×</button>`}</div>${kind === 'referenceImages' ? (readOnly ? `<div class="launch-showcase-reference-fields launch-showcase-reference-fields-readonly"><span>品牌：${html(plain(detail.brand) || '未填写')}</span><span>月销：${html(plain(detail.monthlySales) || '未填写')}</span></div>` : `<div class="launch-showcase-reference-fields"><label>品牌<input value="${html(plain(detail.brand))}" placeholder="填写参考品牌" data-showcase-reference-field="${theme.path}|${imageIndex}|brand"></label><label>月销<input value="${html(plain(detail.monthlySales))}" placeholder="填写月销量" data-showcase-reference-field="${theme.path}|${imageIndex}|monthlySales"></label></div>`) : ''}</article>`;
  }).join('')}${images.length < 10 && !readOnly ? `<label class="launch-showcase-add"><input type="file" accept="image/*" multiple data-showcase-image-input="${kind}|${theme.path}"><span>＋</span><strong>插入图片</strong><small>上传后自动增加下一格</small></label>` : ''}</div></section>`;
}

function showcaseItemAt(plan: DataRecord, path: string): DataRecord | undefined {
  const parts = path.split('|');
  if (parts[0] === 'basic') {
    const products = parts[1] === 'subwave' ? records(plan.subwaveBasicProducts) : records(plan.basicProducts);
    return products[Number(parts[2])];
  }
  const [segmentIndex, sceneIndex, themeIndex] = path.split(':').map(Number);
  if (segmentIndex === undefined || sceneIndex === undefined || themeIndex === undefined) return undefined;
  return records(records(records(plan.segments)[segmentIndex]?.scenes)[sceneIndex]?.themes)[themeIndex];
}

function showcaseImageField(path: string, kind: string): string {
  return path.startsWith('basic|') && kind === 'finalImages' ? 'images' : kind;
}

async function saveShowcasePlanChange(planId: string, modal: HTMLElement, mutate: (plan: DataRecord) => void, rerender: boolean): Promise<void> {
  const run = async () => {
    const status = modal.querySelector<HTMLElement>('.launch-showcase-save-status');
    if (status) { status.textContent = '正在自动保存…'; status.dataset.kind = ''; }
    try {
      const plans = await launchPlanClient.list();
      const plan = plans.find(item => String(item.id ?? '') === planId);
      if (!plan) throw new Error('没有找到这份产品开发企划');
      mutate(plan);
      const saved = await launchPlanClient.save(plan);
      const panel = modal.querySelector<HTMLElement>('.launch-analysis-panel');
      if (rerender && panel) panel.innerHTML = renderPlanAnalysis('showcase', saved.data);
      const nextStatus = modal.querySelector<HTMLElement>('.launch-showcase-save-status');
      if (nextStatus) { nextStatus.textContent = saved.savedOffline ? '已保存到本机，联网后同步' : '已自动保存'; nextStatus.dataset.kind = ''; }
    } catch (error) {
      const nextStatus = modal.querySelector<HTMLElement>('.launch-showcase-save-status');
      if (nextStatus) { nextStatus.textContent = error instanceof Error ? `保存失败：${error.message}` : '保存失败，请重试'; nextStatus.dataset.kind = 'error'; }
    }
  };
  const queued = showcaseSaveChain.then(run, run);
  showcaseSaveChain = queued.catch(() => undefined);
  await queued;
}

async function handleShowcaseImageChange(event: Event, modal: HTMLElement, planId: string): Promise<void> {
  const input = (event.target as Element | null)?.closest<HTMLInputElement>('[data-showcase-image-input]');
  if (!input) return;
  const [kind, ...pathParts] = (input.dataset.showcaseImageInput ?? '').split('|');
  const path = pathParts.join('|');
  const files = [...(input.files ?? [])].filter(file => file.type.startsWith('image/'));
  if (!kind || !path || !files.length) return;
  input.disabled = true;
  const dataUrls = await Promise.all(files.map(readShowcaseImage));
  await saveShowcasePlanChange(planId, modal, plan => {
    const item = showcaseItemAt(plan, path);
    if (!item) return;
    const imageField = showcaseImageField(path, kind);
    const current = strings(item[imageField]);
    const additions = dataUrls.slice(0, Math.max(0, 10 - current.length));
    item[imageField] = [...current, ...additions];
    if (kind === 'referenceImages') {
      const details = records(item.referenceImageDetails);
      while (details.length < current.length + additions.length) details.push({ id: `reference-${Date.now()}-${details.length}`, brand: '', monthlySales: '' });
      item.referenceImageDetails = details;
    }
  }, true);
}

function handleShowcaseTextInput(event: Event, modal: HTMLElement, planId: string): void {
  const themeField = (event.target as Element | null)?.closest<HTMLTextAreaElement>('[data-showcase-theme-field]');
  if (themeField) {
    const parts = (themeField.dataset.showcaseThemeField ?? '').split('|');
    const field = parts.pop() ?? '';
    const path = parts.join('|');
    if (!path || !field) return;
    const timerKey = `${planId}|${path}|${field}`;
    const existing = showcaseSaveTimers.get(timerKey);
    if (existing) clearTimeout(existing);
    const status = modal.querySelector<HTMLElement>('.launch-showcase-save-status');
    if (status) status.textContent = '等待自动保存…';
    const value = themeField.value;
    showcaseSaveTimers.set(timerKey, setTimeout(() => {
      showcaseSaveTimers.delete(timerKey);
      void saveShowcasePlanChange(planId, modal, plan => {
        const item = showcaseItemAt(plan, path);
        if (item) item[field] = value;
      }, false);
    }, 500));
    return;
  }
  const input = (event.target as Element | null)?.closest<HTMLInputElement>('[data-showcase-reference-field]');
  if (!input) return;
  const parts = (input.dataset.showcaseReferenceField ?? '').split('|');
  const field = parts.pop() ?? '';
  const imageIndex = Number(parts.pop());
  const path = parts.join('|');
  if (!path || !field || !Number.isInteger(imageIndex)) return;
  const timerKey = `${planId}|${path}|${imageIndex}|${field}`;
  const existing = showcaseSaveTimers.get(timerKey);
  if (existing) clearTimeout(existing);
  const status = modal.querySelector<HTMLElement>('.launch-showcase-save-status');
  if (status) status.textContent = '等待自动保存…';
  const value = input.value;
  showcaseSaveTimers.set(timerKey, setTimeout(() => {
    showcaseSaveTimers.delete(timerKey);
    void saveShowcasePlanChange(planId, modal, plan => {
      const item = showcaseItemAt(plan, path);
      if (!item) return;
      const details = records(item.referenceImageDetails);
      while (details.length <= imageIndex) details.push({ id: `reference-${Date.now()}-${details.length}`, brand: '', monthlySales: '' });
      const detail = details[imageIndex] ?? { id: `reference-${Date.now()}-${imageIndex}`, brand: '', monthlySales: '' };
      detail[field] = value;
      details[imageIndex] = detail;
      item.referenceImageDetails = details;
    }, false);
  }, 500));
}

async function handleProductAttributeChange(event: Event, modal: HTMLElement, planId: string): Promise<void> {
  const select = (event.target as Element | null)?.closest<HTMLSelectElement>('[data-product-attribute]');
  if (!select || select.disabled) return;
  const path = select.dataset.productAttribute ?? '';
  if (!path || !planId) return;
  const value = select.value;
  const previousValue = select.dataset.previousValue ?? '';
  const run = async () => {
    try {
      const plans = await launchPlanClient.list();
      const plan = plans.find(item => String(item.id ?? '') === planId);
      const product = plan ? productAttributeItemAt(plan, path) : undefined;
      if (!plan || !product) throw new Error('没有找到这款产品，请刷新清单后重试。');
      product.productAttribute = value;
      await launchPlanClient.save(plan);
      select.dataset.previousValue = value;
    } catch (error) {
      select.value = previousValue;
      window.alert(error instanceof Error ? error.message : '产品属性保存失败，请重试。');
    } finally {
    }
  };
  const queued = productAttributeSaveChain.then(run, run);
  productAttributeSaveChain = queued.catch(() => undefined);
  await queued;
}

function readShowcaseImage(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('图片读取失败'));
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.readAsDataURL(file);
  });
}

const productAttributeOptions = ['主推款', '形象款', '第一批产品', '淘汰款'] as const;

const productListColumns: Array<[string, string]> = [
  ['style', '产品风格'],
  ['category', '产品品类'],
  ['theme', '产品主题'],
  ['image', '产品图片'],
  ['name', '产品名称'],
  ['plannedRetailPrice', '计划售价'],
  ['plannedDevelopmentPrice', '计划开发价格'],
  ['productMultiplier', '产品倍率'],
  ['material', '材质'],
  ['element', '元素'],
  ['productAttribute', '产品属性'],
];

export function renderProductList(recordsList: Array<Record<string, string>>, sorts: Array<{ field: string; direction: 'asc' | 'desc' }> = [], options: { readOnly?: boolean } = {}): string {
  const items = sortProductList(recordsList.filter(item => Boolean(item.image)).map(item => ({
    ...item,
    productMultiplier: calculateProductMultiplier(item.plannedRetailPrice ?? '', item.plannedDevelopmentPrice ?? ''),
  })), sorts);
  const sortMap = new Map(sorts.map((sort, index) => [sort.field, { ...sort, index }]));
  const header = productListColumns.map(([field, label]) => {
    const active = sortMap.get(field);
    const marker = active ? `${active.direction === 'asc' ? ' ↑' : ' ↓'}${sorts.length > 1 ? ` ${active.index + 1}` : ''}` : '';
    return `<th class="product-list-sortable"><button type="button" data-product-list-sort="${html(field)}" title="点击排序；按住 Shift 点击可叠加排序">${html(label)}${marker}</button></th>`;
  }).join('');
  const body = items.length ? items.map((item, index) => `<tr>
    <td>${content(item.style)}</td>
    <td>${content(item.category)}</td>
    <td>${content(item.theme)}</td>
    <td>${item.image ? `<button type="button" class="product-list-image" data-wave-report-image="${html(item.image)}" data-wave-report-alt="${html(item.name || `产品 ${index + 1}`)}" aria-label="放大查看${html(item.name || `产品 ${index + 1}`)}"><img src="${html(item.image)}" alt="${html(item.name || `产品 ${index + 1}`)}"><span>查看大图</span></button>` : '<em>未上传</em>'}</td>
    <td>${content(item.name)}</td>
    <td>${content(item.plannedRetailPrice)}</td>
    <td>${content(item.plannedDevelopmentPrice)}</td>
    <td>${content(item.productMultiplier)}</td>
    <td>${content(item.material)}</td>
    <td>${content(item.element)}</td>
    <td>${renderProductAttributeSelect(item, options.readOnly)}</td>
  </tr>`).join('') : `<tr><td colspan="11">${emptyAnalysis('尚未上传改款后的成品图')}</td></tr>`;
  return `<div class="launch-product-list"><p class="launch-product-list-tip">共 ${items.length} 款已上传改款成品；点击表头排序，按住 Shift 点击可添加第二、第三排序条件。${options.readOnly ? '' : ' 产品属性可直接在最右侧选择，修改后自动保存。'}</p><div class="launch-product-list-scroll" data-product-list-scroll><table><thead><tr>${header}</tr></thead><tbody>${body}</tbody></table></div><div class="launch-product-list-floating-scroll" data-product-list-floating-scroll aria-label="产品清单横向滚动条" tabindex="0"><div data-product-list-floating-content></div></div></div>`;
}

function setupProductListScroll(modal: HTMLElement): void {
  const root = modal.querySelector<HTMLElement>('.launch-product-list');
  const tableScroll = root?.querySelector<HTMLElement>('[data-product-list-scroll]');
  const floatingScroll = root?.querySelector<HTMLElement>('[data-product-list-floating-scroll]');
  const floatingContent = root?.querySelector<HTMLElement>('[data-product-list-floating-content]');
  if (!root || !tableScroll || !floatingScroll || !floatingContent) return;
  let syncing = false;
  const syncSize = () => {
    floatingContent.style.width = `${tableScroll.scrollWidth}px`;
    const hasOverflow = tableScroll.scrollWidth > tableScroll.clientWidth + 1;
    root.classList.toggle('has-product-list-overflow', hasOverflow);
    if (!hasOverflow) return;
    floatingScroll.scrollLeft = tableScroll.scrollLeft;
  };
  tableScroll.addEventListener('scroll', () => {
    if (syncing) return;
    syncing = true;
    floatingScroll.scrollLeft = tableScroll.scrollLeft;
    syncing = false;
  }, { passive: true });
  floatingScroll.addEventListener('scroll', () => {
    if (syncing) return;
    syncing = true;
    tableScroll.scrollLeft = floatingScroll.scrollLeft;
    syncing = false;
  }, { passive: true });
  const resizeObserver = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(syncSize);
  resizeObserver?.observe(tableScroll);
  resizeObserver?.observe(tableScroll.querySelector('table') ?? tableScroll);
  syncSize();
}

function renderProductAttributeSelect(item: Record<string, string>, readOnly = false): string {
  const path = item.productPath || '';
  if (!path) return content(item.productAttribute);
  const disabled = readOnly ? ' disabled' : '';
  const options = productAttributeOptions.map(value => `<option value="${html(value)}"${item.productAttribute === value ? ' selected' : ''}>${html(value)}</option>`).join('');
  return `<select class="product-list-attribute" data-product-attribute="${html(path)}" data-previous-value="${html(item.productAttribute || '')}" aria-label="${html(item.name || '产品')}的产品属性"${disabled}><option value="">请选择</option>${options}</select>`;
}

export function calculateProductMultiplier(plannedRetailPrice: string, plannedDevelopmentPrice: string): string {
  const retailValues = priceNumbers(plannedRetailPrice);
  const developmentValues = priceNumbers(plannedDevelopmentPrice);
  const developmentPrice = developmentValues[0] ?? 0;
  if (!retailValues.length || developmentPrice <= 0) return '';
  const multipliers = retailValues.map(value => formatMultiplier(value / developmentPrice));
  return `${multipliers.length > 1 ? `${multipliers[0]}–${multipliers.at(-1)}` : multipliers[0]}×`;
}

function priceNumbers(value: string): number[] {
  return [...(value || '').matchAll(/[0-9]+(?:\.[0-9]+)?/g)]
    .map(match => Number.parseFloat(match[0]))
    .filter(number => Number.isFinite(number));
}

function formatMultiplier(value: number): string {
  return (Math.round(value * 100 + 1e-9) / 100).toFixed(2).replace(/\.00$/, '').replace(/(\.\d)0$/, '$1');
}

function sortProductList(recordsList: Array<Record<string, string>>, sorts: Array<{ field: string; direction: 'asc' | 'desc' }>): Array<Record<string, string>> {
  if (!sorts.length) return [...recordsList];
  const priceValue = (value: string): number => Number.parseFloat((value || '').match(/[0-9]+(?:\.[0-9]+)?/)?.[0] ?? '') || 0;
  return [...recordsList].sort((left, right) => {
    for (const sort of sorts) {
      const leftValue = left[sort.field] || '';
      const rightValue = right[sort.field] || '';
      const numeric = sort.field === 'plannedRetailPrice' || sort.field === 'plannedDevelopmentPrice' || sort.field === 'productMultiplier';
      const compared = numeric ? priceValue(leftValue) - priceValue(rightValue) : leftValue.localeCompare(rightValue, 'zh-CN', { numeric: true, sensitivity: 'base' });
      if (compared !== 0) return sort.direction === 'asc' ? compared : -compared;
    }
    return 0;
  });
}

function boardSelect(key: string, label: string, values: string[]): string {
  return `<label><span>${label}</span><select data-board-filter="${key}"><option value="">全部</option>${values.map(value => `<option value="${html(value)}">${html(value)}</option>`).join('')}</select></label>`;
}

function filterBoardRecords(recordsList: Array<Record<string, string>>, filters: Record<string, string>): Array<Record<string, string>> {
  const filtered = recordsList.filter(item => (!filters.category || item.category === filters.category) && (!filters.style || item.styleLine === filters.style) && (!filters.designer || (filters.designer === '未指定设计师' ? !item.designer : item.designer === filters.designer)));
  const levelRank: Record<string, number> = { S: 0, A: 1, B: 2, C: 3 };
  return filtered.sort((left, right) => {
    if (filters.sort === 'price-desc' || filters.sort === 'price-asc') {
      const leftPrice = Number.parseFloat((left.price || '').match(/[0-9]+(?:\.[0-9]+)?/)?.[0] ?? '') || 0;
      const rightPrice = Number.parseFloat((right.price || '').match(/[0-9]+(?:\.[0-9]+)?/)?.[0] ?? '') || 0;
      return filters.sort === 'price-desc' ? rightPrice - leftPrice : leftPrice - rightPrice;
    }
    return (levelRank[left.level || ''] ?? 9) - (levelRank[right.level || ''] ?? 9);
  });
}

function renderBoardGroups(recordsList: Array<Record<string, string>>, groupBy = 'category'): string {
  const visibleRecords = recordsList.filter(item => Boolean(item.image));
  if (!visibleRecords.length) return emptyAnalysis('暂无已上传改款成品图的实际开发款');
  const groups = new Map<string, Array<Record<string, string>>>();
  for (const item of visibleRecords) {
    const field = groupBy === 'style' ? 'styleLine' : groupBy;
    const key = item[field] || (field === 'category' ? '未填写品类' : field === 'styleLine' ? '未填写风格线' : '未指定设计师');
    const group = groups.get(key) ?? [];
    group.push(item);
    groups.set(key, group);
  }
  return `<div class="launch-product-board-groups">${[...groups.entries()].map(([group, items]) => `<section class="launch-product-board-group"><header><strong>${html(group)}</strong><span>${items.length} 款</span></header><div class="launch-analysis-board">${items.map((item, index) => `<article><div class="launch-analysis-board-media">${item.image ? renderBoardImage(item.image, item.name || `产品 ${index + 1}`) : '<span>暂无实际产品图</span>'}</div><strong>${html(item.name || `产品 ${index + 1}`)}</strong><p>${html([item.designer ? `设计师：${item.designer}` : '未指定设计师', item.level ? `${item.level}款` : '', item.category, item.styleLine, item.price].filter(Boolean).join(' · ') || '尚未填写产品信息')}</p></article>`).join('')}</div></section>`).join('')}</div>`;
}

function renderBoardImage(src: string, alt: string): string {
  return `<button type="button" class="launch-analysis-board-image" data-wave-report-image="${html(src)}" data-wave-report-alt="${html(alt)}" aria-label="放大查看${html(alt)}"><img src="${html(src)}" alt="${html(alt)}"><span>查看大图</span></button>`;
}

function analysisPanel(title: string, body: string): string { return `<section class="launch-analysis-panel-inner"><header><strong>${html(title)}</strong><span>当前企划实时汇总</span></header>${body}</section>`; }
function emptyAnalysis(message: string): string { return `<div class="launch-analysis-empty">${html(message)}</div>`; }

export function renderWaveReport(plan: DataRecord): string {
  const segments = records(plan.segments);
  const subwaveBasics = records(plan.subwaveBasicProducts);
  const basics = subwaveBasics.length ? subwaveBasics : records(plan.basicProducts);
  const collaboration = record(plan._collaboration);
  const importance = plain(plan.importanceLevel);
  const counts = segments.reduce<{ scenes: number; themes: number; styles: number }>((total, segment) => {
    const scenes = records(segment.scenes);
    total.scenes += scenes.length;
    for (const scene of scenes) {
      const themes = records(scene.themes);
      total.themes += themes.length;
      for (const theme of themes) total.styles += records(theme.styles).length;
    }
    return total;
  }, { scenes: 0, themes: 0, styles: 0 });

  return `
    <header class="wave-report-header">
      <div>
        <div class="wave-report-kicker">WAVE PLANNING PACKAGE / 波段企划打包</div>
        <h2>${html(plain(plan.name) || `${formatDate(plan.launchDate)}产品开发计划`)}</h2>
        <p>本报告由当前波段计划即时生成，完整汇总计划内的文字、时间、图片、场景、主题、逐款资料、视觉推广建议及基本款。</p>
      </div>
      <div class="wave-report-summary">
        <span>${segments.length} 个风格板块</span><span>${counts.scenes} 个场景</span><span>${counts.themes} 个主题</span><span>${counts.styles + basics.length} 个产品记录</span>
      </div>
    </header>

    <div class="wave-report-body">
      ${reportSection('01', '波段基础信息', `
        <div class="wave-report-info-grid">
          ${field('计划名称', plan.name)}
          ${field('正式上架时间', formatDate(plan.launchDate))}
          ${field('波段等级', importance ? `${importance} · ${importanceLabels[importance] || '自定义等级'}` : '')}
          ${field('创建人', collaboration.creatorName || plan.creatorName || collaboration.createdBy || plan.createdBy)}
          ${field('创建时间', formatDateTime(plan.createdAt))}
          ${field('最后更新时间', formatDateTime(collaboration.updatedAt || plan.updatedAt))}
          ${field('重要性说明', plan.importanceReason, true)}
          ${field('本周期改款规划', plan.cyclePlan, true)}
        </div>
      `)}

      ${reportSection('02', '风格 × 品类开发矩阵', renderAssortmentMatrix(plan))}

      ${reportSection('03', '完整开发时间轴', `
        <div class="wave-report-timeline">
          ${timelineFields.map(([label, start, end]) => `
            <div class="wave-report-timeline-row"><strong>${label}</strong><span>${formatDate(plan[start])}</span><i>→</i><span>${formatDate(plan[end])}</span></div>
          `).join('')}
          <div class="wave-report-timeline-row final"><strong>正式上市</strong><span>${formatDate(plan.launchDate)}</span></div>
        </div>
      `)}

      ${reportSection('04', '本周期全部参考图', `
        <div class="wave-report-reference-groups">
          ${renderImages('AJIDOU品牌款式参考', plan.ajidouReferenceImages)}
          ${renderCompetitorReferences(plan)}
        </div>
      `)}

      ${reportSection('05', '本月开发计划 · 色彩占比分析', renderColorAnalysis(plan))}

      ${reportSection('06', '本期波段重点推广产品', renderFeaturedProducts(plan))}

      ${reportSection('07', '四大主题风格产品企划', `
        <div class="wave-report-segment-toolbar">
          <p>每列展示一个完整风格，内容按场景与主题自然向下延伸；使用左右按钮或横向滑动切换其他风格及子波段基本款。</p>
          <div class="wave-report-segment-controls" aria-label="切换风格企划">
            <button type="button" data-wave-report-segment-scroll="prev" aria-label="查看上一个风格">←</button>
            <button type="button" data-wave-report-segment-scroll="next" aria-label="查看下一个风格">→</button>
          </div>
        </div>
        <div class="wave-report-segments">
          ${segments.length ? segments.map((segment, index) => renderSegment(segment, index)).join('') : renderEmptySegment()}
          ${renderBasicSegment(basics)}
        </div>
      `)}
    </div>
  `;
	}

function renderFeaturedProducts(plan: DataRecord): string {
  const homepage = records(plan.homepageFeaturedProducts).slice(0, 6);
  const offsite = records(plan.offsiteFeaturedProducts).slice(0, 10);
  const card = (item: DataRecord, index: number, label: string) => `<article class="wave-report-featured-card"><div class="wave-report-featured-media">${plain(item.image) ? renderBoardImage(plain(item.image), `${label} ${index + 1}`) : '<span>暂无图片</span>'}</div><div><strong>${html(label)} ${String(index + 1).padStart(2, '0')}</strong><p>${html(plain(item.brief) || '尚未填写产品简报')}</p></div></article>`;
  return `<div class="wave-report-featured"><section><header><strong>首页主推款清单</strong><span>${homepage.length} / 6 款</span></header><div class="wave-report-featured-grid">${homepage.length ? homepage.map((item, index) => card(item, index, '首页主推款')).join('') : emptyAnalysis('尚未建立首页主推款')}</div></section><section><header><strong>站外推广主推款</strong><span>${offsite.length} / 10 款</span></header><div class="wave-report-featured-list">${offsite.length ? offsite.map((item, index) => card(item, index, '站外推广主推款')).join('') : emptyAnalysis('尚未建立站外推广主推款')}</div></section></div>`;
}

function renderColorAnalysis(plan: DataRecord): string {
  const accentImages = strings(plan.accentColorAnalysisImages).slice(0, 5);
  const baseImages = strings(plan.baseColorAnalysisImages).slice(0, 5);
  return `
    <div class="wave-report-color-analysis">
      <p class="wave-report-color-intro">同步展示本月开发计划中的产品点缀色系与产品底色颜色参考，并保留对应的占比、搭配关系与落地说明。</p>
      <div class="wave-report-color-list">
        <section class="wave-report-color-row">
          <div>
            ${renderImages('产品点缀色系分析', accentImages)}
            ${field('色彩使用说明', plan.accentColorAnalysisNote, true)}
          </div>
          <span class="wave-report-color-role">点缀色｜强调识别、层次与主题氛围</span>
        </section>
        <section class="wave-report-color-row">
          <div>
            ${renderImages('产品底色颜色分析', baseImages)}
            ${field('色彩使用说明', plan.baseColorAnalysisNote, true)}
          </div>
          <span class="wave-report-color-role">底色｜决定系列基调、材质呈现与搭配基础</span>
        </section>
      </div>
    </div>`;
}

function renderAssortmentMatrix(plan: DataRecord): string {
  const matrix = record(plan.assortmentMatrix);
  const categorySource = Array.isArray(matrix.categories) ? matrix.categories : [];
  const categories = Array.from({ length: 5 }, (_, index) => plain(categorySource[index]));
  const rowSource = records(matrix.rows);
  const rows = Array.from({ length: 5 }, (_, rowIndex) => {
    const source = rowSource[rowIndex] ?? {};
    const quantities = Array.isArray(source.quantities) ? source.quantities : [];
    return {
      style: plain(source.style),
      quantities: categories.map((_, columnIndex) => Number(quantities[columnIndex]) || 0),
      priceRange: plain(source.priceRange),
      elements: plain(source.elements),
    };
  });
  const totals = categories.map((_, columnIndex) => rows.reduce((sum, row) => sum + (row.quantities[columnIndex] ?? 0), 0));
  const grandTotal = totals.reduce((sum, value) => sum + value, 0);

  return `
    <div class="wave-report-assortment-intro">
      <p>按风格规划各品类开发款数、主力价格段与元素方向；数据与开发计划中的矩阵保持一致。</p>
      <span>自动合计</span>
    </div>
    <div class="wave-report-assortment-wrap">
      <table class="wave-report-assortment-table">
        <thead><tr><th>风格</th>${categories.map((category, index) => `<th>${html(category || `品类${index + 1}`)}</th>`).join('')}<th>主力价格段</th><th>元素</th></tr></thead>
        <tbody>
          ${rows.map((row, rowIndex) => `<tr><th>${html(row.style || `风格${rowIndex + 1}`)}</th>${row.quantities.map(value => `<td>${value}</td>`).join('')}<td>${content(row.priceRange)}</td><td>${content(row.elements)}</td></tr>`).join('')}
          <tr class="total"><th>合计</th>${totals.map(value => `<td>${value}</td>`).join('')}<td>${grandTotal} 款</td><td>数量合计会根据交叉格自动汇总</td></tr>
        </tbody>
      </table>
    </div>`;
}

function renderEmptySegment(): string {
  return `<article class="wave-report-segment wave-report-segment-empty"><header><div><span>STYLE SEGMENT</span><h3>主题风格企划</h3></div><p>尚未建立</p></header>${empty('尚未建立主题风格企划')}</article>`;
}

function renderBasicSegment(basics: DataRecord[]): string {
  const count = basics.length;
  return `
    <article class="wave-report-segment wave-report-basic-segment">
      <header><div><span>ESSENTIAL BASICS</span><h3>子波段基本款</h3></div><p>${count} 个产品</p></header>
      ${count ? `<div class="wave-report-basic-list">${basics.map((product, index) => renderBasicProduct(product, index)).join('')}</div>` : empty('尚未添加子波段基本款')}
    </article>`;
}

function renderSegment(segment: DataRecord, index: number): string {
  const scenes = records(segment.scenes);
  const themeCount = scenes.reduce((sum, scene) => sum + records(scene.themes).length, 0);
  const styleCount = scenes.reduce((sum, scene) => sum + records(scene.themes).reduce((themeSum, theme) => themeSum + records(theme.styles).length, 0), 0);
  return `
    <article class="wave-report-segment">
      <header><div><span>STYLE SEGMENT ${String(index + 1).padStart(2, '0')}</span><h3>${html(plain(segment.label) || `风格板块 ${index + 1}`)}</h3></div><p>${scenes.length} 场景 · ${themeCount} 主题 · ${styleCount} 款式</p></header>
      ${scenes.length ? scenes.map((scene, sceneIndex) => renderScene(scene, sceneIndex)).join('') : empty('该风格板块尚未建立场景')}
    </article>`;
}

function renderScene(scene: DataRecord, sceneIndex: number): string {
  const themes = records(scene.themes);
  return `
    <section class="wave-report-scene">
      <div class="wave-report-scene-title"><span>场景 ${String(sceneIndex + 1).padStart(2, '0')}</span><h4>${html(plain(scene.name) || '场景名称未填写')}</h4></div>
      ${themes.length ? themes.map((theme, themeIndex) => renderTheme(theme, themeIndex)).join('') : empty('该场景尚未建立主题')}
    </section>`;
}

function renderTheme(theme: DataRecord, themeIndex: number): string {
  const styles = records(theme.styles);
  const advice = record(theme.advice);
  return `
    <article class="wave-report-theme is-collapsed">
      <header class="wave-report-theme-head"><button type="button" class="wave-report-theme-toggle" data-wave-report-theme-toggle aria-expanded="false" aria-label="展开主题内容">＋</button><span>主题 ${String(themeIndex + 1).padStart(2, '0')}</span><h5>${html(plain(theme.name) || '主题名称未填写')}</h5></header>
      <div class="wave-report-theme-body">
      <div class="wave-report-info-grid compact">
        ${field('设计师', theme.designer)}
        ${field('计划开发款数', theme.plannedCount)}
        ${field('计划销售价格段', theme.priceBand)}
        ${field('计划材质', theme.materials)}
        ${field('计划工艺', theme.crafts)}
        ${field('设计元素与特点', theme.elements)}
        ${field('开发这个主题的原因', theme.rationale, true)}
        ${field('主题描述 / 改款方向', theme.direction, true)}
      </div>
      <div class="wave-report-image-stack">
        ${renderImages('参考款式', theme.referenceImages, 'single-row')}
        ${renderImages('改款后的成品 / 设计稿', theme.finalImages, 'single-row final-designs')}
      </div>
      <section class="wave-report-advice-card wide wave-report-consumer-portrait">
        <header><span>画像</span><h6>消费者画像</h6></header>
        <div class="wave-report-image-pair">
          ${renderImages('消费者画像参考图', theme.extensionReferenceImages)}
          ${field('消费者画像说明', theme.extensionPlan, true)}
        </div>
      </section>
      <div class="wave-report-subtitle">逐款开发追踪</div>
      ${styles.length ? renderProductTable(styles) : empty('尚未添加实际开发款')}
      <div class="wave-report-subtitle">视觉与推广建议</div>
      ${linkField('参考品牌产品链接', advice.referenceProductUrl)}
      <div class="wave-report-advice-groups">
        <section class="wave-report-advice-card">
          <header><span>01</span><h6>主图与组图拍摄</h6></header>
          ${field('主图 / 组图拍摄建议', advice.heroShoot, true)}
          ${renderImages('主图 / 组图视觉参考', advice.heroShootImages)}
        </section>
        <section class="wave-report-advice-card">
          <header><span>02</span><h6>详情页视觉设计</h6></header>
          ${field('详情页视觉风格建议', advice.detailVisual, true)}
          ${renderImages('详情页视觉参考', advice.detailVisualImages)}
        </section>
        <section class="wave-report-advice-card wide">
          <header><span>03</span><h6>站内与站外推广</h6></header>
          <div class="wave-report-info-grid compact">
            ${field('站内推广建议', advice.onsitePromotion)}
            ${field('站外推广建议', advice.offsitePromotion)}
          </div>
        </section>
        <section class="wave-report-advice-card wide">
          <header><span>04</span><h6>博主合作画像</h6></header>
          <div class="wave-report-info-grid compact">
            ${field('适合合作的博主画像补充', advice.influencerProfile)}
            ${field('适合的博主脸型 / 气质补充', advice.faceProfile)}
          </div>
          <div class="wave-report-image-pair advice-images">
            ${renderImages('适合合作的博主风格', advice.influencerImages)}
            ${renderImages('适合的博主脸型 / 气质', advice.faceImages)}
          </div>
        </section>
        <section class="wave-report-advice-card wide compact-card">
          <header><span>05</span><h6>产品等级定位</h6></header>
          ${field('产品等级建议', advice.productLevel, true)}
        </section>
      </div>
      </div>
    </article>`;
}

function renderBasicProduct(product: DataRecord, index: number): string {
  return `
    <article class="wave-report-basic-product">
      <header><span>直接产品 ${String(index + 1).padStart(2, '0')}</span><h3>${html(plain(product.name) || `产品 ${index + 1}`)}</h3></header>
      <div class="wave-report-image-pair">
        ${renderImages('参考款式图片', product.referenceImages)}
        ${renderImages('穿搭氛围 / 对应人群图片', product.personaImages)}
      </div>
      ${renderImages('确定改款后的产品图片', product.images)}
      <div class="wave-report-info-grid compact">
        ${field('参考品牌', product.referenceBrand)}
        ${field('产品等级', formatProductLevel(product.productLevel))}
        ${field('计划材质', product.plannedMaterial)}
        ${field('计划工艺', product.plannedCraft)}
        ${field('计划元素', product.plannedElements)}
        ${field('对应人群', product.personaProfile, true)}
        ${field('改款方向', product.adaptationDirection, true)}
        ${field('风格', product.style)}
        ${field('元素', product.elements)}
        ${field('主题', product.theme)}
        ${field('材质', product.material)}
        ${field('计划价格', product.plannedPrice)}
        ${field('计划售价', product.plannedRetailPrice || product.plannedPrice)}
        ${field('实际价格', product.actualPrice)}
        ${field('穿搭建议', product.outfit, true)}
        ${field('产品描述 / 开发结果', product.description, true)}
      </div>
    </article>`;
}

function renderProductTable(styles: DataRecord[]): string {
  return `<div class="wave-report-product-list">${styles.map((style, index) => `
    <article class="wave-report-product-card">
      <header><span>开发款 ${String(index + 1).padStart(2, '0')}</span><h6>${content(style.name)}</h6></header>
      <div class="wave-report-product-grid">
        ${trackingField('产品等级', formatProductLevel(style.productLevel))}
        ${trackingField('风格', style.style)}
        ${trackingField('元素', style.elements)}
        ${trackingField('主题', style.theme)}
        ${trackingField('材质', style.material)}
        ${trackingField('计划价格', style.plannedPrice)}
        ${trackingField('实际价格', style.actualPrice)}
        ${trackingField('穿搭建议', style.outfit, true)}
        ${trackingField('产品描述 / 开发结果', style.description, true)}
      </div>
    </article>`).join('')}</div>`;
}

function trackingField(label: string, value: unknown, wide = false): string {
  return `<div class="wave-report-product-field${wide ? ' wide' : ''}"><span>${label}</span><p>${content(value)}</p></div>`;
}

function reportSection(number: string, title: string, body: string): string {
  return `<section class="wave-report-section"><header class="wave-report-section-head"><span>${number}</span><h3>${title}</h3></header>${body}</section>`;
}

function field(label: string, value: unknown, wide = false): string {
  return `<div class="wave-report-field${wide ? ' wide' : ''}"><span>${label}</span><p>${content(value)}</p></div>`;
}

function linkField(label: string, value: unknown): string {
  const raw = plain(value);
  if (!raw) return field(label, '');
  try {
    const url = new URL(raw);
    if (!['http:', 'https:'].includes(url.protocol)) return field(label, raw);
    return `<div class="wave-report-field wide"><span>${label}</span><p><a href="${html(url.toString())}" target="_blank" rel="noreferrer">${html(raw)}</a></p></div>`;
  } catch {
    return field(label, raw);
  }
}

function renderImages(label: string, value: unknown, variant = ''): string {
  const images = strings(value);
  const classes = ['wave-report-images', variant].filter(Boolean).join(' ');
  return `<section class="${classes}"><div class="wave-report-images-head"><strong>${label}</strong><span>${images.length} 张</span></div>${renderImageGrid(images, label)}</section>`;
}

function renderCompetitorReferences(plan: DataRecord): string {
  const storedGroups = records(plan.competitorReferenceGroups);
  const legacyImages = strings(plan.cycleReferenceImages);
  const groups = competitorReferenceDefinitions.map((definition, groupIndex) => {
    const stored = storedGroups.find(group => plain(group.id) === definition.id) ?? storedGroups[groupIndex] ?? {};
    return {
      ...definition,
      title: typeof stored.title === 'string' ? stored.title : definition.title,
      brand: plain(stored.brand),
      images: storedGroups.length ? strings(stored.images).slice(0, 20) : legacyImages.slice(groupIndex * 20, (groupIndex + 1) * 20),
    };
  });
  const totalImages = groups.reduce((total, group) => total + group.images.length, 0);
  return `
    <section class="wave-report-competitor-system">
      <div class="wave-report-competitor-system-head">
        <div><strong>其他竞品参考款式</strong><p>按照四种品牌风格分别呈现参考品牌与对应款式图片。</p></div>
        <span>4个风格 · ${totalImages} 张</span>
      </div>
      <div class="wave-report-competitor-list">
        ${groups.map((group, groupIndex) => `
          <section class="wave-report-competitor-group" data-competitor-style="${groupIndex + 1}">
            <div class="wave-report-competitor-group-head">
              <div>
                <strong>${html(group.title)}</strong>
                <div class="wave-report-competitor-brand"><span>参考品牌</span><b>${group.brand ? html(group.brand) : '暂未填写'}</b></div>
              </div>
              <span>${group.images.length} / 20 张</span>
            </div>
            ${renderImageGrid(group.images, `${group.title}${group.brand ? ` · ${group.brand}` : ''}`)}
          </section>
        `).join('')}
      </div>
    </section>`;
}

function renderImageGrid(images: string[], label: string): string {
  return images.length
    ? `<div class="wave-report-image-grid">${images.map((src, index) => `<button type="button" class="wave-report-image" data-wave-report-image="${html(src)}" data-wave-report-alt="${html(`${label} ${index + 1}`)}" aria-label="放大查看${html(label)}第 ${index + 1} 张"><img src="${html(src)}" alt="${html(label)} ${index + 1}"><span>查看大图</span></button>`).join('')}</div>`
    : empty('该风格暂未上传参考图片');
}

function empty(message: string): string {
  return `<div class="wave-report-empty">${html(message)}</div>`;
}

function openReportLightbox(src: string, alt: string): void {
  if (!src) return;
  let lightbox = document.querySelector<HTMLElement>('.wave-report-lightbox');
  if (!lightbox) {
    lightbox = document.createElement('div');
    lightbox.className = 'wave-report-lightbox';
    lightbox.innerHTML = '<button type="button" aria-label="关闭图片预览">×</button><img alt=""><p></p>';
    lightbox.addEventListener('click', event => {
      if (event.target === lightbox || (event.target as Element).closest('button')) lightbox?.classList.remove('show');
    });
    document.body.append(lightbox);
  }
  const image = lightbox.querySelector('img');
  const caption = lightbox.querySelector('p');
  if (image) { image.src = src; image.alt = alt; }
  if (caption) caption.textContent = alt;
  lightbox.classList.add('show');
}

function record(value: unknown): DataRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value) ? value as DataRecord : {};
}

function records(value: unknown): DataRecord[] {
  return Array.isArray(value) ? value.map(record) : [];
}

function strings(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string' && item.length > 0) : [];
}

function plain(value: unknown): string {
  if (value === null || value === undefined) return '';
  return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' ? String(value).trim() : '';
}

function content(value: unknown): string {
  const valueText = plain(value);
  return valueText ? html(valueText).replace(/\n/g, '<br>') : '<em>未填写</em>';
}

function formatDate(value: unknown): string {
  const raw = plain(value);
  if (!raw) return '未填写';
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
  return match ? `${match[1]}年${Number(match[2])}月${Number(match[3])}日` : raw;
}

function formatDateTime(value: unknown): string {
  const raw = plain(value);
  if (!raw) return '未填写';
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? raw : date.toLocaleString('zh-CN', { hour12: false });
}

function formatProductLevel(value: unknown): string {
  const level = plain(value).toUpperCase();
  return ['S', 'A', 'B', 'C'].includes(level) ? `${level}款` : '';
}

function html(value: string): string {
  return value.replace(/[&<>"']/g, character => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[character] ?? character);
}
